// src/pages/.../ManageMembers/ManageMembers.jsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaArrowRight,
  FaCheckCircle,
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";

import { useNotification } from "../../../components/Notification/NotificationManager";

import AdherentModal from "./AdherentModal";
import UploadAdherentsModal from "./UploadAdherentsModal";
import ManageAdherentGroupsModal from "./ManageAdherentGroupsModal";

import ManageMembersToolbar from "./components/ManageMembersToolbar";
import MembersList from "./components/MembersList";

import useManageMembers from "./hooks/useManageMembers";
import useMembersFilters from "./hooks/useMembersFilters";

import { getPlanConfig } from "./data/manageMembers.data";

import {
  adherentBelongsToGroup,
  cn,
} from "./utils/manageMembers.utils";

import {
  exportAdherentsForGroup,
  exportAllAdherentsByGroups,
} from "./utils/exportAdherentsExcel";

const ManageMembers = () => {
  useScrollToTop();

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const {
    roleId,
    demoMode,

    club,
    clubId,
    adherents,
    dojos,
    groups,

    loading,
    submitting,

    refreshAdherents,
    handleDeleteAdherent,
  } = useManageMembers(addNotification);

  const {
    searchTerm,
    selectedDojo,
    selectedGroup,

    filteredGroups,
    filteredAdherents,

    setSearchTerm,
    setSelectedGroup,
    handleDojoChange,
  } = useMembersFilters({
    adherents,
    groups,
  });

  const [adherentModalOpen, setAdherentModalOpen] =
    useState(false);

  const [selectedAdherent, setSelectedAdherent] =
    useState(null);

  const [uploadModalOpen, setUploadModalOpen] =
    useState(false);

  const [groupModalOpen, setGroupModalOpen] =
    useState(false);

  const [
    selectedAdherentForGroups,
    setSelectedAdherentForGroups,
  ] = useState(null);

  const planConfig = getPlanConfig(roleId);
  const adherentLimit = planConfig.limit;

  const totalAdherents = adherents.length;

  const hasReachedLimit =
    adherentLimit !== Infinity &&
    totalAdherents >= adherentLimit;

  const progressPercentage =
    adherentLimit === Infinity
      ? 0
      : Math.min(
          (totalAdherents / adherentLimit) * 100,
          100,
        );

  const openCreateModal = () => {
    if (demoMode) {
      addNotification(
        "Mode Démo : ajout désactivé.",
        "warning",
      );

      return;
    }

    if (hasReachedLimit) {
      addNotification(
        `Vous avez atteint la limite de ${adherentLimit} adhérents pour votre formule.`,
        "error",
      );

      return;
    }

    setSelectedAdherent(null);
    setAdherentModalOpen(true);
  };

  const openEditModal = (adherent) => {
    if (demoMode) {
      addNotification(
        "Mode Démo : modification désactivée.",
        "warning",
      );

      return;
    }

    setSelectedAdherent(adherent);
    setAdherentModalOpen(true);
  };

  const openUploadModal = () => {
    if (demoMode) {
      addNotification(
        "Mode Démo : importation désactivée.",
        "warning",
      );

      return;
    }

    if (hasReachedLimit) {
      addNotification(
        `Vous avez atteint la limite de ${adherentLimit} adhérents pour votre formule.`,
        "error",
      );

      return;
    }

    setUploadModalOpen(true);
  };

  const openManageGroups = (adherent) => {
    if (demoMode) {
      addNotification(
        "Mode Démo : gestion désactivée.",
        "warning",
      );

      return;
    }

    setSelectedAdherentForGroups(adherent);
    setGroupModalOpen(true);
  };

  const handleExportSelectedGroup = () => {
    if (!selectedGroup) {
      addNotification(
        "Veuillez sélectionner un groupe avant de lancer l'export.",
        "warning",
      );

      return;
    }

    const group = groups.find(
      (currentGroup) =>
        String(currentGroup?.id) ===
        String(selectedGroup),
    );

    if (!group) {
      addNotification(
        "Le groupe sélectionné est introuvable.",
        "error",
      );

      return;
    }

    const groupAdherentsCount = adherents.filter(
      (adherent) =>
        adherentBelongsToGroup(
          adherent,
          selectedGroup,
        ),
    ).length;

    if (groupAdherentsCount === 0) {
      addNotification(
        "Ce groupe ne contient actuellement aucun adhérent.",
        "warning",
      );

      return;
    }

    try {
      const exportedCount = exportAdherentsForGroup({
        adherents,
        group,
        clubName: club?.nom_club,
      });

      addNotification(
        `${exportedCount} adhérent${
          exportedCount > 1 ? "s" : ""
        } exporté${
          exportedCount > 1 ? "s" : ""
        } pour le groupe ${group.nom_groupe}.`,
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur lors de l'export du groupe :",
        error,
      );

      addNotification(
        "Erreur lors de la création du fichier Excel.",
        "error",
      );
    }
  };

  const handleExportAllGroups = () => {
    if (!adherents.length) {
      addNotification(
        "Aucun adhérent à exporter.",
        "warning",
      );

      return;
    }

    try {
      const exportedCount =
        exportAllAdherentsByGroups({
          adherents,
          groups,
          clubName: club?.nom_club,
        });

      addNotification(
        `${exportedCount} adhérent${
          exportedCount > 1 ? "s" : ""
        } exporté${
          exportedCount > 1 ? "s" : ""
        } avec les feuilles par groupe.`,
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur lors de l'export général :",
        error,
      );

      addNotification(
        "Erreur lors de la création du fichier Excel.",
        "error",
      );
    }
  };

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Votre compte
              </p>

              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Adhérents
              </h1>

              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Gérez vos membres, affectez-les rapidement à
                leurs groupes et exportez vos listes au format
                Excel.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {planConfig.label && (
                <span
                  className={cn(
                    "inline-flex rounded-full px-4 py-2 text-sm font-semibold",
                    planConfig.badgeClass,
                  )}
                >
                  {planConfig.label}
                  {" · "}
                  {adherentLimit === Infinity
                    ? "Adhérents illimités"
                    : `${adherentLimit} adhérents max`}
                </span>
              )}

              {demoMode && (
                <span className="inline-flex rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  Mode Démo
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="mt-5">
          {loading ? (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto h-24 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
                <div className="mx-auto mt-4 h-5 w-40 rounded bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            </div>
          ) : !club ? (
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <FaUsers className="text-4xl" />
              </div>

              <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
                Aucun club trouvé
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Créez d’abord votre club avant de gérer vos
                adhérents.
              </p>

              <button
                type="button"
                onClick={() =>
                  navigate("/votre-club")
                }
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Aller à votre club
                <FaArrowRight />
              </button>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                    Club
                  </p>

                  <h2 className="mt-2 text-2xl font-black">
                    {club.nom_club}
                  </h2>

                  <p className="mt-2 text-sm text-white/80">
                    {club.departement_club ||
                      "Département non renseigné"}
                  </p>
                </div>

                <div className="p-6 text-center">
                  {club.logo_url ? (
                    <img
                      src={club.logo_url}
                      alt={club.nom_club}
                      className="mx-auto h-24 w-24 rounded-full border-4 border-white object-cover shadow-xl ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                    />
                  ) : (
                    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-xl dark:bg-slate-800 dark:text-slate-300">
                      <FaUsers className="text-4xl" />
                    </div>
                  )}

                  <p className="mt-5 text-sm text-slate-600 dark:text-slate-400">
                    {club.adresse_club ||
                      "Adresse non renseignée"}
                  </p>
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUsers />
                    </div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Total adhérents
                    </p>

                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {totalAdherents}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaLayerGroup />
                    </div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Résultats filtrés
                    </p>

                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {filteredAdherents.length}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <FaCheckCircle />
                    </div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Limite
                    </p>

                    <p className="mt-2 text-lg font-black text-slate-900 dark:text-slate-100">
                      {adherentLimit === Infinity
                        ? "Illimitée"
                        : `${totalAdherents} / ${adherentLimit}`}
                    </p>
                  </div>
                </div>

                {adherentLimit !== Infinity && (
                  <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-700 dark:text-slate-300">
                        Utilisation de la formule
                      </span>

                      <span className="text-slate-500 dark:text-slate-400">
                        {Math.round(progressPercentage)}%
                      </span>
                    </div>

                    <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          progressPercentage >= 100
                            ? "bg-red-500"
                            : progressPercentage >= 80
                              ? "bg-amber-500"
                              : "bg-blue-600 dark:bg-blue-500",
                        )}
                        style={{
                          width: `${progressPercentage}%`,
                        }}
                      />
                    </div>

                    {hasReachedLimit && (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                        Vous avez atteint la limite de{" "}
                        {adherentLimit} adhérents pour votre
                        formule.
                        <br />

                        <a
                          href="/formules-tarifs"
                          className="mt-2 inline-block font-semibold text-blue-600 hover:underline dark:text-blue-400"
                        >
                          Passer à une formule supérieure
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {!loading && club && (
          <>
            <ManageMembersToolbar
              searchTerm={searchTerm}
              selectedDojo={selectedDojo}
              selectedGroup={selectedGroup}
              dojos={dojos}
              filteredGroups={filteredGroups}
              demoMode={demoMode}
              hasReachedLimit={hasReachedLimit}
              hasAdherents={adherents.length > 0}
              onSearchChange={setSearchTerm}
              onDojoChange={handleDojoChange}
              onGroupChange={setSelectedGroup}
              onAdd={openCreateModal}
              onImport={openUploadModal}
              onExportSelectedGroup={
                handleExportSelectedGroup
              }
              onExportAllGroups={
                handleExportAllGroups
              }
            />

            <MembersList
              adherents={filteredAdherents}
              demoMode={demoMode}
              submitting={submitting}
              onManageGroups={openManageGroups}
              onEdit={openEditModal}
              onDelete={handleDeleteAdherent}
            />
          </>
        )}
      </div>

      <AdherentModal
        isOpen={adherentModalOpen}
        onClose={() => {
          setAdherentModalOpen(false);
          setSelectedAdherent(null);
        }}
        adherent={selectedAdherent}
        clubId={clubId}
        onUpdate={refreshAdherents}
        demoMode={demoMode}
      />

      <UploadAdherentsModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpdate={refreshAdherents}
        clubId={clubId}
        demoMode={demoMode}
      />

      <ManageAdherentGroupsModal
        isOpen={groupModalOpen}
        onClose={() => {
          setGroupModalOpen(false);
          setSelectedAdherentForGroups(null);
        }}
        adherent={selectedAdherentForGroups}
        adherentGroups={
          selectedAdherentForGroups?.Groups || []
        }
        clubId={clubId}
        onUpdate={refreshAdherents}
        demoMode={demoMode}
      />
    </div>
  );
};

export default ManageMembers;